import clsx from 'clsx';

import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';

import { TTrendDirection } from '@/types';

interface ISummaryCardProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;

  /** Variant */
  variant: 'highlight' | 'dashboard';

  /** Highlight card props */
  bg?: string;
  text?: string;
  sub?: string;

  /** Dashboard card props */
  trend?: number | string;
  trendDirection?: TTrendDirection | string;
  isPositive?: boolean;
  trendLabel?: string;

  className?: string;
  dataTestId?: string;
}

export const SummaryCard: React.FC<ISummaryCardProps> = ({
  label,
  value,
  icon,
  variant,
  bg = '',
  text = '',
  sub = '',
  trend,
  trendDirection,
  trendLabel,
  className = '',
  dataTestId = 'summary-card',
}) => {
  /* HIGHLIGHT VARIANT */
  if (variant === 'highlight') {
    return (
      <div
        className={clsx(
          'py-2.5 px-4 rounded-lg flex flex-col justify-center items-center',
          bg,
          className
        )}
        data-testid={dataTestId}
      >
        <div className={clsx('text-xs font-semibold leading-5', sub)}>
          {label}
        </div>

        <div className="flex items-center justify-center mt-1 gap-1">
          {icon && (
            <div className="flex items-center justify-center">{icon}</div>
          )}
          <div
            className={clsx('text-2xl font-semibold', text)}
            data-testid={`${dataTestId}-value`}
          >
            {value}
          </div>
        </div>
      </div>
    );
  }

  const getTrendClass = () => {
    if (trendDirection === 'UP') {
      return 'bg-success-light-50 text-success-default';
    }
    if (trendDirection === 'DOWN') {
      return 'bg-error-light-50 text-error-default';
    }
    return 'bg-neutral-light-100 text-neutral-secondary';
  };

  /* DASHBOARD VARIANT */
  return (
    <div
      className={clsx(
        'flex flex-row items-center justify-between rounded-xl border border-neutral-light-200 bg-card shadow-2xs p-4 gap-2.5',
        className
      )}
      data-testid={dataTestId}
    >
      <span className="p-2 rounded-lg border border-neutral-light-200 shadow-2xs">
        {icon}
      </span>

      <div className="flex flex-row justify-between space-y-1.5 w-full gap-4">
        <div className="flex justify-center flex-col items-start mb-0 w-full">
          <p className="text-sm leading-5 font-semibold text-neutral-primary">
            {label}
          </p>
          <div className="flex flex-row justify-between space-y-1.5 w-full gap-4">
            <span
              className="text-2xl leading-8 font-semibold text-neutral-primary"
              data-testid={`${dataTestId}-value`}
            >
              {value}
            </span>
            {trendLabel && (
              <div className="flex justify-center items-end gap-1.5">
                <span className="flex flex-col lg:flex-row justify-end items-center gap-1.5">
                  <span
                    className={clsx(
                      'inline-flex items-center rounded-full px-2 py-1 text-sm font-medium gap-1',
                      getTrendClass()
                    )}
                  >
                    {trendDirection === 'UP' && <IconArrowUp size={16} />}
                    {trendDirection === 'DOWN' && <IconArrowDown size={16} />}
                    {trend}
                  </span>
                  <p className="text-sm text-neutral-secondary">{trendLabel}</p>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
